import * as THREE from 'three';

import type { EnvironmentMaterialSet } from './materials';
import type { QualityProfile } from './quality';
import type { EnvironmentThemeDefinition } from './themes';

export interface SharedEnvironmentGeometry {
  readonly plotTop: THREE.BoxGeometry;
  readonly plotEdge: THREE.BoxGeometry;
  readonly pathTile: THREE.BoxGeometry;
  readonly treeCrown: THREE.ConeGeometry;
  readonly treeTrunk: THREE.CylinderGeometry;
  readonly hedge: THREE.BoxGeometry;
  readonly lampPost: THREE.CylinderGeometry;
  readonly lampGlobe: THREE.SphereGeometry;
  readonly stone: THREE.DodecahedronGeometry;
}

export interface PlotDimensions {
  readonly width: number;
  readonly depth: number;
  readonly centerX?: number;
  readonly centerZ?: number;
  readonly groundElevation?: number;
}

export const DEFAULT_PLOT_DIMENSIONS: PlotDimensions = Object.freeze({ width: 160, depth: 120 });

export interface ProceduralEnvironmentOptions {
  readonly theme: EnvironmentThemeDefinition;
  readonly quality: QualityProfile;
  readonly materials: EnvironmentMaterialSet;
  readonly geometries?: SharedEnvironmentGeometry;
  readonly seed?: number;
  readonly plot?: PlotDimensions;
}

export function createSharedEnvironmentGeometry(
  profile: QualityProfile,
  plot: PlotDimensions = DEFAULT_PLOT_DIMENSIONS,
): SharedEnvironmentGeometry {
  return {
    plotTop: new THREE.BoxGeometry(plot.width, 1.1, plot.depth),
    plotEdge: new THREE.BoxGeometry(plot.width + 4, 2.5, plot.depth + 4),
    pathTile: new THREE.BoxGeometry(1.8, 0.12, 1.8),
    treeCrown: new THREE.ConeGeometry(0.9, 2.7, profile.radialSegments),
    treeTrunk: new THREE.CylinderGeometry(0.13, 0.18, 1.2, profile.radialSegments),
    hedge: new THREE.BoxGeometry(1.4, 1, 1),
    lampPost: new THREE.CylinderGeometry(0.06, 0.09, 2.5, profile.radialSegments),
    lampGlobe: new THREE.SphereGeometry(
      0.18,
      profile.radialSegments,
      Math.max(4, profile.radialSegments - 2),
    ),
    stone: new THREE.DodecahedronGeometry(0.7, 0),
  };
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function setInstance(
  mesh: THREE.InstancedMesh,
  index: number,
  position: THREE.Vector3,
  scale: THREE.Vector3,
  rotationY: number,
): void {
  const transform = new THREE.Object3D();
  transform.position.copy(position);
  transform.scale.copy(scale);
  transform.rotation.y = rotationY;
  transform.updateMatrix();
  mesh.setMatrixAt(index, transform.matrix);
}

function makeInstances(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  count: number,
  name: string,
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  mesh.name = name;
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  return mesh;
}

export function createProceduralEnvironment(options: ProceduralEnvironmentOptions): THREE.Group {
  const { theme, quality, materials } = options;
  const plot = options.plot ?? DEFAULT_PLOT_DIMENSIONS;
  const geometries = options.geometries ?? createSharedEnvironmentGeometry(quality, plot);
  const random = seededRandom(options.seed ?? 0xbca57e);
  const root = new THREE.Group();
  root.name = `environment:${theme.id}`;
  root.position.set(plot.centerX ?? 0, plot.groundElevation ?? 0, plot.centerZ ?? 0);

  const edge = new THREE.Mesh(geometries.plotEdge, materials.plotEdge);
  edge.name = 'plot-edge';
  edge.position.y = -1.4;
  edge.receiveShadow = true;
  const top = new THREE.Mesh(geometries.plotTop, materials.plotTop);
  top.name = 'plot-top';
  top.position.y = -0.25;
  top.receiveShadow = true;
  root.add(edge, top);

  const horizontalPathCount = Math.max(2, Math.floor(plot.width / 2));
  const verticalPathCount = Math.max(2, Math.floor(plot.depth / 2));
  const pathCount = horizontalPathCount + verticalPathCount;
  const paths = makeInstances(geometries.pathTile, materials.path, pathCount, 'park-paths');
  for (let index = 0; index < pathCount; index += 1) {
    const row = index < horizontalPathCount;
    const rowIndex = row ? index : index - horizontalPathCount;
    const count = row ? horizontalPathCount : verticalPathCount;
    const offset = rowIndex - (count - 1) / 2;
    const position = row
      ? new THREE.Vector3(offset * 1.85, 0.36, 0)
      : new THREE.Vector3(0, 0.36, offset * 1.85);
    setInstance(paths, index, position, new THREE.Vector3(1, 1, 1), 0);
  }
  root.add(paths);

  const ambientCap = quality.tier === 'low' ? 24 : quality.tier === 'medium' ? 48 : 72;
  const totalScenery = Math.max(12, Math.round(ambientCap * quality.sceneryDensity));
  const treeCount = Math.round(totalScenery * theme.scenery.treeRatio);
  const hedgeCount = Math.round(totalScenery * theme.scenery.hedgeRatio);
  const lampCount = Math.max(2, totalScenery - treeCount - hedgeCount);
  const crowns = makeInstances(geometries.treeCrown, materials.foliage, treeCount, 'tree-crowns');
  const trunks = makeInstances(geometries.treeTrunk, materials.trunk, treeCount, 'tree-trunks');
  const hedges = makeInstances(geometries.hedge, materials.foliageAccent, hedgeCount, 'hedges');
  const lampPosts = makeInstances(geometries.lampPost, materials.stone, lampCount, 'lamp-posts');
  const lampGlobes = makeInstances(
    geometries.lampGlobe,
    materials.practical,
    lampCount,
    'lamp-globes',
  );
  crowns.castShadow = quality.shadows;
  trunks.castShadow = quality.shadows;
  hedges.castShadow = quality.shadows;
  lampPosts.castShadow = quality.smallDetailShadows;
  lampGlobes.castShadow = false;

  for (let index = 0; index < treeCount; index += 1) {
    const side = random() < 0.5 ? -1 : 1;
    const position = new THREE.Vector3(
      side * (plot.width * 0.32 + random() * plot.width * 0.14),
      1.22,
      -plot.depth * 0.42 + random() * plot.depth * 0.84,
    );
    const size = 0.7 + random() * 0.7;
    setInstance(crowns, index, position, new THREE.Vector3(size, size, size), random() * Math.PI);
    setInstance(
      trunks,
      index,
      new THREE.Vector3(position.x, 0.62, position.z),
      new THREE.Vector3(size, size, size),
      0,
    );
  }
  for (let index = 0; index < hedgeCount; index += 1) {
    const horizontal = random() < 0.5;
    const position = horizontal
      ? new THREE.Vector3(
          -plot.width * 0.44 + random() * plot.width * 0.88,
          0.82,
          random() < 0.5 ? -18 : 18,
        )
      : new THREE.Vector3(
          random() < 0.5 ? -24 : 24,
          0.82,
          -plot.depth * 0.4 + random() * plot.depth * 0.8,
        );
    const scale = horizontal ? new THREE.Vector3(1.5, 1, 0.75) : new THREE.Vector3(0.75, 1, 1.5);
    setInstance(hedges, index, position, scale, 0);
  }
  for (let index = 0; index < lampCount; index += 1) {
    const position = new THREE.Vector3(
      -12 + (index % 2) * 24,
      1.5,
      -plot.depth * 0.36 + (index / Math.max(1, lampCount - 1)) * plot.depth * 0.72,
    );
    setInstance(lampPosts, index, position, new THREE.Vector3(1, 1, 1), 0);
    setInstance(
      lampGlobes,
      index,
      new THREE.Vector3(position.x, 2.78, position.z),
      new THREE.Vector3(1, 1, 1),
      0,
    );
  }
  root.add(crowns, trunks, hedges, lampPosts, lampGlobes);

  const stoneCount = Math.max(5, Math.round(12 * quality.sceneryDensity));
  const stones = makeInstances(geometries.stone, materials.stone, stoneCount, 'accent-stones');
  stones.castShadow = quality.smallDetailShadows;
  for (let index = 0; index < stoneCount; index += 1) {
    const position = new THREE.Vector3(
      -plot.width * 0.44 + random() * plot.width * 0.88,
      0.68,
      random() < 0.5 ? -plot.depth * 0.44 : plot.depth * 0.44,
    );
    const size = 0.45 + random() * 0.5;
    setInstance(stones, index, position, new THREE.Vector3(size, size, size), random() * Math.PI);
  }
  root.add(stones);

  return root;
}

export function disposeSharedEnvironmentGeometry(geometries: SharedEnvironmentGeometry): void {
  const owned: THREE.BufferGeometry[] = [
    geometries.plotTop,
    geometries.plotEdge,
    geometries.pathTile,
    geometries.treeCrown,
    geometries.treeTrunk,
    geometries.hedge,
    geometries.lampPost,
    geometries.lampGlobe,
    geometries.stone,
  ];
  for (const geometry of owned) geometry.dispose();
}
